import {
    DashboardDateFilter,
    DashboardFilter,
    DashboardFilterType,
    DashboardMultiFilter,
    DashboardTenantFilter,
} from "@quillsql/react"
import { ChangeEvent, useCallback } from "react"

import QuillDateRangePicker from "@pages/Reports/ReportDashboard/components/QuillDateRangePicker/QuillDateRangePicker"
import QuillMultiSelect from "@pages/Reports/ReportDashboard/components/QuillMultiSelect/QuillMultiSelect"
import QuillSelect from "@pages/Reports/ReportDashboard/components/QuillSelect/QuillSelect"

import styles from "./DashboardFilterBar.module.scss"
import { ApplyFilterValue, DashboardFilterBarProps } from "./DashboardFilterBar.types"

type MultiSelectChangeEvent = ChangeEvent<HTMLSelectElement> | { target: { value: (string | null)[] } }

function getCurrentFilterValue(filter: DashboardFilter): ApplyFilterValue {
    switch (filter.type) {
        case DashboardFilterType.Select:
            return filter.value
        case DashboardFilterType.MultiSelect:
            return filter.value
        case DashboardFilterType.Date:
            return {
                startDate: filter.value.startDate,
                endDate: filter.value.endDate,
                preset: filter.value.presetValue,
            }
        case DashboardFilterType.Tenant:
            if (Array.isArray(filter.value)) {
                return filter.value.map(String)
            }
            return filter.value != null ? String(filter.value) : ""
        default:
            throw new Error(`Unhandled filter type: ${(filter as DashboardFilter).type}`)
    }
}

function SingleSelectFilter(props: {
    filter: DashboardFilter & { type: typeof DashboardFilterType.Select }
    onChangeFilter: (label: string, value: ApplyFilterValue) => void
}) {
    const { filter, onChangeFilter } = props

    return (
        <QuillSelect
            label={filter.label}
            value={filter.value}
            options={filter.options.map((option) => ({ label: option.label, value: option.value }))}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => onChangeFilter(filter.label, event.target.value)}
            width={160}
        />
    )
}

function MultiSelectFilter(props: {
    filter: DashboardMultiFilter
    onChangeFilter: (label: string, value: ApplyFilterValue) => void
}) {
    const { filter, onChangeFilter } = props

    return (
        <QuillMultiSelect
            label={filter.label}
            value={filter.value}
            options={filter.options.map((option) => ({ label: option.label, value: option.value }))}
            onChange={(event: MultiSelectChangeEvent) => {
                const selected = event.target.value as (string | null)[]
                onChangeFilter(
                    filter.label,
                    selected.filter((val): val is string => val != null),
                )
            }}
            width={160}
        />
    )
}

function DateFilter(props: {
    filter: DashboardDateFilter
    onChangeFilter: (label: string, value: ApplyFilterValue) => void
}) {
    const { filter, onChangeFilter } = props

    return (
        <QuillDateRangePicker
            label={filter.label}
            preset={filter.value.presetValue ?? ""}
            dateRange={{
                startDate: filter.value.startDate,
                endDate: filter.value.endDate,
            }}
            presetOptions={filter.options.map((option) => ({ label: option.label, value: option.value }))}
            onChangeDateRange={({ startDate, endDate }) => {
                onChangeFilter(filter.label, { startDate, endDate })
            }}
            onChangePreset={(event: ChangeEvent<HTMLSelectElement>) => {
                const presetValue = event.target.value
                const presetOption = filter.options.find((option) => option.value === presetValue)
                if (presetOption) {
                    onChangeFilter(filter.label, {
                        startDate: presetOption.startDate,
                        endDate: presetOption.endDate,
                        preset: presetOption.value,
                    })
                }
            }}
        />
    )
}

function TenantFilter(props: {
    filter: DashboardTenantFilter
    onChangeFilter: (label: string, value: ApplyFilterValue) => void
}) {
    const { filter, onChangeFilter } = props

    const options = filter.options.map((option) => ({
        label: option.label,
        value: String(option.value),
    }))

    if (Array.isArray(filter.value)) {
        return (
            <QuillMultiSelect
                label={filter.label}
                value={filter.value.map(String)}
                options={options}
                onChange={(event: MultiSelectChangeEvent) => {
                    const selected = event.target.value as (string | null)[]
                    onChangeFilter(
                        filter.label,
                        selected.filter((val): val is string => val != null),
                    )
                }}
                width={160}
            />
        )
    }

    const currentValue = filter.value != null ? String(filter.value) : ""

    return (
        <QuillSelect
            label={filter.label}
            value={currentValue}
            options={options}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => onChangeFilter(filter.label, event.target.value)}
            width={160}
        />
    )
}

export default function DashboardFilterBar(props: DashboardFilterBarProps) {
    const { filters, onApplyFilters } = props

    const handleChangeFilter = useCallback(
        (changedLabel: string, changedValue: ApplyFilterValue) => {
            const updatedFilters = filters.map((filter) => ({
                label: filter.label,
                value: filter.label === changedLabel ? changedValue : getCurrentFilterValue(filter),
            }))
            onApplyFilters(updatedFilters)
        },
        [filters, onApplyFilters],
    )

    if (filters.length === 0) {
        return null
    }

    return (
        <div className={styles.base}>
            {filters.map((filter) => {
                switch (filter.type) {
                    case DashboardFilterType.Select:
                        return (
                            <SingleSelectFilter
                                key={filter.label}
                                filter={filter}
                                onChangeFilter={handleChangeFilter}
                            />
                        )
                    case DashboardFilterType.MultiSelect:
                        return (
                            <MultiSelectFilter
                                key={filter.label}
                                filter={filter}
                                onChangeFilter={handleChangeFilter}
                            />
                        )
                    case DashboardFilterType.Date:
                        return <DateFilter key={filter.label} filter={filter} onChangeFilter={handleChangeFilter} />
                    case DashboardFilterType.Tenant:
                        return <TenantFilter key={filter.label} filter={filter} onChangeFilter={handleChangeFilter} />
                    default:
                        throw new Error(`Unhandled filter type: ${(filter as DashboardFilter).type}`)
                }
            })}
        </div>
    )
}
